import React, { useEffect, useState } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import ProviderPreview from './ProviderPreview';
import DataList from '@afosto/app-components-react/DataList';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import Page from '@afosto/app-components-react/Page';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import uuid from '@afosto/utils/uuid';
import { providers as providersApi } from '@afosto/order/api/Providers';
import translations from './translations';
import PaymentProviderCard, {
  PaymentProviderCardSkeleton,
} from '../../components/PaymentProviderCard';

const AFOSTO_APP_URL = 'https://app.afosto.io/';

/**
 * Fetch all providers.
 * @returns {Promise<[]|Array>}
 */
const fetchProviders = async ({ pagination = {} }) => {
  try {
    const { activePage, itemsPerPage, setItemCount } = pagination;
    const response = await providersApi.listProviders();
    const { data = [] } = response;

    const formattedProviders = data.reduce(
      (acc, provider) => [...acc, { ...provider, id: uuid() }],
      [],
    );

    setItemCount(formattedProviders.length);

    return formattedProviders.slice(activePage * itemsPerPage, (activePage + 1) * itemsPerPage);
  } catch (err) {
    return [];
  }
};

const ProvidersContainer = props => <Grid container spacing={2} {...props} />;

const Providers = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:providers:read']);

  const intl = useIntl();
  const [provider, setProvider] = useState(null);
  const dataSource = useDataGrid(fetchProviders, {
    modules: ['dataProperties', 'pagination', 'activeItem'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'name',
          name: intl.formatMessage({
            id: 'dataGrid.columns.providers.name',
            defaultMessage: 'Name',
            description: 'The column name for column name',
          }),
        },
        {
          key: 'code',
          name: intl.formatMessage({
            id: 'dataGrid.columns.providers.code',
            defaultMessage: 'Code',
            description: 'The column name for column code',
          }),
        },
        {
          key: 'is_active',
          name: intl.formatMessage({
            id: 'dataGrid.columns.providers.isActive',
            defaultMessage: 'Active',
            description: 'The column name for column is active',
          }),
          type: 'boolean',
        },
        {
          key: 'is_configured',
          name: intl.formatMessage({
            id: 'dataGrid.columns.providers.isConfigured',
            defaultMessage: 'Configured',
            description: 'The column name for column is configured',
          }),
          type: 'boolean',
        },
      ],
    },
  });
  const { data: dataSourceData = [], activeItem: activeItemModule = {} } = dataSource;
  const { activeItem, setActiveItem } = activeItemModule;

  /**
   * Close the provider preview
   */
  const handleCloseProviderPreview = () => {
    dataSource.activeItem.setActiveItem(null);
    setProvider(null);
  };

  const previewMenu = [
    {
      key: 'edit',
      translationKey: 'actions.edit',
      label: 'Edit',
      icon: 'pencil',
      shortcut: true,
      onClick: () => {
        window.open(`${AFOSTO_APP_URL}settings/credentials`, '_blank');
      },
    },
  ];

  /**
   * Fetch the provider data if a provider row is selected and show the data in the preview.
   */
  useEffect(() => {
    if (activeItem) {
      setProvider(findObjectInArray(dataSourceData, 'id', activeItem));
    } else {
      setProvider(null);
    }
  }, [activeItem, dataSourceData]);

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Grid container wrap="nowrap">
      <Page title={intl.formatMessage(translations.title)} fullWidth style={{ flexGrow: 1 }}>
        <Hidden implementation="js" mdUp>
          <DataList
            dataSource={dataSource}
            component={ProvidersContainer}
            renderItem={item => (
              <PaymentProviderCard item={item} onClick={() => setActiveItem(item.id)} />
            )}
            renderSkeletonItem={PaymentProviderCardSkeleton}
          />
        </Hidden>
        <Hidden implementation="js" smDown>
          <DataGrid dataSource={dataSource} />
        </Hidden>
      </Page>
      <ProviderPreview
        isOpen={dataSource.activeItem.activeItem !== null}
        isLoading={false}
        provider={provider}
        onClose={handleCloseProviderPreview}
        previewMenu={previewMenu}
      />
    </Grid>
  );
};

export default Providers;
